import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  DEFAULT_HITTER_POINTS,
  DEFAULT_PITCHER_POINTS,
} from "@convex/_scoring/rulesets";
import {
  HITTER_KEYS,
  PITCHER_KEYS,
} from "@/features/leagues/scoring/constants";
import { useAdminAuth } from "@/features/admin/hooks/useAdminAuth";
import { AdminShell } from "@/features/admin/shell/AdminShell";
import { AdminPageHeader } from "@/features/admin/shell/AdminPageHeader";
import { EditableRulesCard } from "@/features/admin/scoring/components/EditableRulesCard";
import { CutLastPicksCard } from "@/features/admin/scoring/components/CutLastPicksCard";
import { Button } from "@/shared/ui/Button";
import { useT } from "@/shared/i18n/LocaleContext";

export const Route = createFileRoute("/_adminGuarded/admin/scoring")({
  component: AdminScoringPage,
});

function AdminScoringPage() {
  const t = useT();
  const { state } = useAdminAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const liveRules = useQuery(api.scoringRules.getLiveRules);

  const rescore = useMutation(api.admin.rescoreAllStats);
  const purge = useMutation(api.admin.purgeAllStats);
  const updateHitter = useMutation(api.scoringRules.updateHitterRules);
  const updatePitcher = useMutation(api.scoringRules.updatePitcherRules);

  type RunStatus =
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "done"; count: number }
    | { kind: "error"; message: string };

  type PurgeStatus =
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "done"; deletedStats: number; resetTotals: number }
    | { kind: "error"; message: string };

  const [status, setStatus] = useState<RunStatus>({ kind: "idle" });
  const [purgeStatus, setPurgeStatus] = useState<PurgeStatus>({ kind: "idle" });

  async function handleRescore() {
    if (!token) return;
    if (!window.confirm(t("admin.scoring.rescoreConfirm"))) return;
    setStatus({ kind: "running" });
    try {
      const result = await rescore({ token });
      setStatus({ kind: "done", count: result.rescoredCount });
    } catch (e) {
      setStatus({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function handlePurge() {
    if (!token) return;
    if (!window.confirm(t("admin.scoring.purgeConfirm1"))) return;
    if (!window.confirm(t("admin.scoring.purgeConfirm2"))) return;
    setPurgeStatus({ kind: "running" });
    try {
      const result = await purge({ token });
      setPurgeStatus({
        kind: "done",
        deletedStats: result.deletedStats,
        resetTotals: result.resetTotals,
      });
    } catch (e) {
      setPurgeStatus({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }

  const hitterRevision = liveRules?.hitter.revision ?? 0;
  const pitcherRevision = liveRules?.pitcher.revision ?? 0;

  return (
    <AdminShell breadcrumbs={[{ label: t("admin.scoring.title") }]}>
      <AdminPageHeader
        title={t("admin.scoring.title")}
        subtitle={t("admin.scoring.subtitle")}
        meta={
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted px-2 py-1 rounded-sm border border-border-strong shrink-0">
            rev {hitterRevision}
          </span>
        }
      />

      <div className="space-y-6">
        <EditableRulesCard
          title={t("admin.scoring.title")}
          keys={HITTER_KEYS}
          defaults={DEFAULT_HITTER_POINTS}
          live={liveRules?.hitter.points ?? null}
          labelPrefix="admin.scoring.rules"
          testIdPrefix="hitter"
          disabled={!token}
          onSave={async (points) => {
            if (!token) return;
            await updateHitter({ token, points });
          }}
        />

        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl lg:text-2xl font-semibold tracking-tight text-ink leading-tight">
              {t("admin.scoring.pitcherTitle")}
            </h2>
            <p className="text-muted mt-1 text-sm max-w-prose">
              {t("admin.scoring.pitcherSubtitle")}
            </p>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted px-2 py-1 rounded-sm border border-border-strong shrink-0">
            rev {pitcherRevision}
          </span>
        </div>

        <EditableRulesCard
          title={t("admin.scoring.pitcherTitle")}
          keys={PITCHER_KEYS}
          defaults={DEFAULT_PITCHER_POINTS}
          live={liveRules?.pitcher.points ?? null}
          labelPrefix="admin.scoring.pitcherRules"
          testIdPrefix="pitcher"
          disabled={!token}
          onSave={async (points) => {
            if (!token) return;
            await updatePitcher({ token, points });
          }}
        />

        <div className="border-t border-border pt-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted mb-2">
            {t("admin.scoring.maintenance")}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              disabled={!token || status.kind === "running"}
              onClick={() => void handleRescore()}
            >
              {status.kind === "running"
                ? t("admin.scoring.rescoreRunning")
                : t("admin.scoring.rescoreButton")}
            </Button>
            {status.kind === "done" ? (
              <span className="text-sm text-muted">
                {t("admin.scoring.rescoreDone", { count: String(status.count) })}
              </span>
            ) : null}
            {status.kind === "error" ? (
              <span className="text-sm text-danger">{status.message}</span>
            ) : null}
          </div>
          <p className="text-muted mt-2 text-xs max-w-prose">
            {t("admin.scoring.rescoreHint")}
          </p>
        </div>

        <div className="rounded-md border border-danger/30 bg-danger-soft/30 p-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-danger mb-2">
            {t("admin.scoring.dangerZone")}
          </p>
          <p className="font-display text-base font-semibold text-ink leading-tight">
            {t("admin.scoring.purgeTitle")}
          </p>
          <p className="text-muted mt-1 text-sm max-w-prose">
            {t("admin.scoring.purgeSubtitle")}
          </p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Button
              variant="danger"
              size="sm"
              disabled={!token || purgeStatus.kind === "running"}
              onClick={() => void handlePurge()}
            >
              {purgeStatus.kind === "running"
                ? t("admin.scoring.purgeRunning")
                : t("admin.scoring.purgeButton")}
            </Button>
            {purgeStatus.kind === "done" ? (
              <span className="text-sm text-muted">
                {t("admin.scoring.purgeDone", {
                  stats: String(purgeStatus.deletedStats),
                  totals: String(purgeStatus.resetTotals),
                })}
              </span>
            ) : null}
            {purgeStatus.kind === "error" ? (
              <span className="text-sm text-danger">{purgeStatus.message}</span>
            ) : null}
          </div>
        </div>

        <CutLastPicksCard token={token} />
      </div>
    </AdminShell>
  );
}
